import { faSolidKeyCirclePlus } from '@awesome.me/kit-09b03e5df4/icons/kit/custom';
import { css } from '@emotion/react';
import { faKeySkeletonLeftRight } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useEffect,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { RadioComponent } from '~/_shared/baseComponents/radio/radio.component';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { useTranslation } from '~/_shared/utils/hooks';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import { useTimeout } from '~/_shared/utils/hooks/useTimeout';
import { PasswordOption } from './mapPrivacySelector.types';

const containerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 14,
});

const iconStyle = css({
  fontSize: 22,
});

type PasswordOptionSelectorProps = Readonly<{
  preservePasswordDisabledReason?: string;
  selectedValue: PasswordOption | null;
  onValueChange: (value: PasswordOption) => void;
}>;

export const PasswordOptionSelectorComponent: FC<PasswordOptionSelectorProps> = ({
  selectedValue,
  onValueChange,
  preservePasswordDisabledReason,
}) => {
  const [t] = useTranslation();
  const [isPreserveDisabledReasonShown, showPreserveDisabledReason] = useTimeout(6000);
  const previousPreservePasswordDisabledReason = usePrevious(preservePasswordDisabledReason);

  const items = [
    {
      value: PasswordOption.preserve,
      icon: faKeySkeletonLeftRight,
      label: t('password.keepExisting'),
      disabledReason: preservePasswordDisabledReason,
      forceShowDisabledReason: isPreserveDisabledReasonShown,
    },
    {
      value: PasswordOption.create,
      icon: faSolidKeyCirclePlus,
      label: t('password.createNew'),
    },
  ];

  useEffect(() => {
    if (preservePasswordDisabledReason
      && preservePasswordDisabledReason !== previousPreservePasswordDisabledReason
      && !isPreserveDisabledReasonShown
    ) {
      showPreserveDisabledReason();
    }
  }, [isPreserveDisabledReasonShown, showPreserveDisabledReason, preservePasswordDisabledReason, previousPreservePasswordDisabledReason]);

  return (
    <div css={containerStyle}>
      {items.map((item, index) => {
        const isChecked = item.value === selectedValue;
        return (
          <TooltipComponent
            key={index}
            tooltipContent={item.disabledReason}
            behavior={item.forceShowDisabledReason ? TooltipBehavior.ShowAlways : TooltipBehavior.ShowOnHover}
          >
            <RadioComponent
              isNested
              isChecked={isChecked}
              onChange={() => onValueChange(item.value)}
              label={item.label}
              value={item.value}
              isDisabled={!!item.disabledReason}
              icon={<FontAwesomeIcon css={iconStyle} icon={item.icon} />}
            />
          </TooltipComponent>
        );
      })}
    </div>
  );
};
